import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function IntelligencePanel() {
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [dataIntel, setDataIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);

  const fetchData = async () => {
    try {
      const [intel, price, di] = await Promise.all([
        axios.get(`${API}/intelligence/dashboard`).catch(() => ({ data: null })),
        axios.get(`${API}/pricing/analytics`).catch(() => ({ data: null })),
        axios.get(`${API}/data-intelligence/dashboard`).catch(() => ({ data: null })),
      ]);
      setData(intel.data);
      setPricing(price.data);
      setDataIntel(di.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 30000); return () => clearInterval(i); }, []);

  const runAction = async (action) => {
    setRunning(action);
    try {
      await axios.post(`${API}/intelligence/${action}`, {}, { timeout: 90000 });
      await fetchData();
    } catch (e) { console.error(e); }
    finally { setRunning(null); }
  };

  const dismissNotif = async (id) => {
    await axios.post(`${API}/data-intelligence/dismiss/${id}`).catch(() => {});
    await fetchData();
  };

  if (loading) return <div data-testid="intelligence-loading" style={{ padding: 16, color: "#71717a", fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}>Cargando inteligencia...</div>;

  const iq = data?.intelligence_score || 0;
  const qa = data?.avg_quality_score || 0;
  const live = data?.products_live || 0;
  const killed = data?.products_killed || 0;
  const roi = pricing?.overall_roi_pct || -100;
  const totalCost = pricing?.total_cost_invested_usd || 0;
  const totalRev = pricing?.total_revenue_usd || 0;

  const vc = data?.latest_improvements?.value_chain_analysis || {};

  return (
    <div data-testid="intelligence-panel" style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: iq > 50 ? "#4ade80" : iq > 25 ? "#facc15" : "#f87171", boxShadow: `0 0 6px ${iq > 50 ? "rgba(74,222,128,0.5)" : iq > 25 ? "rgba(250,204,21,0.4)" : "rgba(248,113,113,0.4)"}` }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#e4e4e7", fontFamily: "'IBM Plex Mono', monospace" }}>INTELLIGENCE</span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[
            { key: "performance-review", label: "P&L", color: "#f87171" },
            { key: "improve", label: "OPT", color: "#60a5fa" },
          ].map(btn => (
            <button key={btn.key} data-testid={`run-${btn.key}-btn`} onClick={() => runAction(btn.key)} disabled={running === btn.key}
              style={{ padding: "3px 8px", fontSize: 8, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em", background: running === btn.key ? "rgba(255,255,255,0.05)" : `${btn.color}20`, color: running === btn.key ? "#71717a" : btn.color, border: `1px solid ${running === btn.key ? "rgba(255,255,255,0.06)" : `${btn.color}30`}`, borderRadius: 3, cursor: running === btn.key ? "wait" : "pointer" }}>
              {running === btn.key ? "..." : btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scores Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
        {[
          { label: "IQ", value: `${iq}`, color: iq > 50 ? "#4ade80" : iq > 25 ? "#facc15" : "#f87171" },
          { label: "QA", value: `${qa}`, color: qa > 70 ? "#4ade80" : qa > 40 ? "#facc15" : "#f87171" },
          { label: "P&L", value: `$${totalRev}`, color: totalRev > 0 ? "#4ade80" : "#f87171" },
          { label: "ROI", value: `${roi}%`, color: roi > 0 ? "#4ade80" : "#f87171" },
        ].map((s, i) => (
          <div key={i} style={{ padding: "6px 8px", textAlign: "center", background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: s.color, fontFamily: "'Chivo', sans-serif" }}>{s.value}</div>
            <div style={{ fontSize: 7, color: "#52525b", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "'IBM Plex Mono', monospace" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Value Chain */}
      {Object.keys(vc).length > 0 && (
        <div style={{ padding: "6px 10px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
          <div style={{ fontSize: 8, fontWeight: 700, color: "#71717a", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4, fontFamily: "'IBM Plex Mono', monospace" }}>Value Chain</div>
          <div style={{ display: "flex", gap: 3 }}>
            {Object.entries(vc).map(([key, val]) => {
              const score = val?.score || 0;
              const barColor = score >= 7 ? "#4ade80" : score >= 4 ? "#facc15" : "#f87171";
              return (
                <div key={key} style={{ flex: 1, textAlign: "center" }} title={`${key}: ${val?.improvement || ""}`}>
                  <div style={{ height: 24, background: "rgba(255,255,255,0.04)", borderRadius: 2, overflow: "hidden", display: "flex", alignItems: "flex-end" }}>
                    <div style={{ width: "100%", height: `${score * 10}%`, background: barColor, borderRadius: 2, transition: "height 0.5s" }} />
                  </div>
                  <div style={{ fontSize: 7, color: "#52525b", marginTop: 2, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {key.replace("_", " ").slice(0, 8)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Human Notifications (intelligence upgrades needed) */}
      {dataIntel?.human_notifications?.length > 0 && (
        <div style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
          {dataIntel.human_notifications.map(n => (
            <div key={n.id} data-testid={`notif-${n.id}`} style={{
              padding: "6px 10px", background: "rgba(250,204,21,0.06)",
              borderBottom: "1px solid rgba(250,204,21,0.1)",
              display: "flex", gap: 6, alignItems: "flex-start",
            }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#facc15", marginTop: 3, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#facc15", fontFamily: "'IBM Plex Mono', monospace" }}>
                  {n.priority}: {n.title}
                </div>
                <div style={{ fontSize: 8, color: "#a1a1aa", fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1.3, marginTop: 2 }}>
                  {n.message}
                </div>
              </div>
              <button onClick={() => dismissNotif(n.id)} style={{
                padding: "2px 4px", fontSize: 8, color: "#52525b", background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2, cursor: "pointer",
                fontFamily: "'IBM Plex Mono', monospace",
              }}>OK</button>
            </div>
          ))}
        </div>
      )}

      {/* Data Coverage */}
      {dataIntel?.intelligence?.intelligence_coverage_pct != null && (
        <div style={{ padding: "6px 10px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
            <span style={{ fontSize: 8, fontWeight: 700, color: "#71717a", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "'IBM Plex Mono', monospace" }}>Data Coverage</span>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#60a5fa", fontFamily: "'IBM Plex Mono', monospace" }}>
              {dataIntel.intelligence.intelligence_coverage_pct}%
            </span>
          </div>
          <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${dataIntel.intelligence.intelligence_coverage_pct}%`,
              background: "linear-gradient(90deg, #60a5fa, #a78bfa)",
              borderRadius: 2, transition: "width 0.5s",
            }} />
          </div>
          <div style={{ fontSize: 7, color: "#52525b", marginTop: 2, fontFamily: "'IBM Plex Mono', monospace" }}>
            {dataIntel.intelligence.current_variables}/{dataIntel.intelligence.total_possible_variables} variables
          </div>
        </div>
      )}

      {/* Product P&L List */}
      <div data-testid="product-pnl" style={{ flex: 1, overflow: "auto" }}>
        {pricing?.products?.map((p, i) => {
          const marginColor = p.margin_pct > 80 ? "#4ade80" : p.margin_pct > 50 ? "#facc15" : "#f87171";
          return (
            <div key={i} style={{ padding: "6px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: p.status === "live" ? "#4ade80" : "#f87171", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#e4e4e7", fontFamily: "'IBM Plex Mono', monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.brand}</div>
                <div style={{ fontSize: 8, color: "#52525b", fontFamily: "'IBM Plex Mono', monospace" }}>
                  ${p.price_usd} | C:${p.cost_usd.toFixed(2)} | V:{p.views}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: marginColor, fontFamily: "'IBM Plex Mono', monospace" }}>{p.margin_pct.toFixed(0)}%</div>
                <div style={{ fontSize: 7, color: "#52525b", fontFamily: "'IBM Plex Mono', monospace" }}>margin</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Cost Summary */}
      <div style={{ padding: "6px 10px", borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)", flexShrink: 0, display: "flex", justifyContent: "space-between", fontSize: 9, fontFamily: "'IBM Plex Mono', monospace" }}>
        <span style={{ color: "#f87171" }}>Inv: ${totalCost.toFixed(2)}</span>
        <span style={{ color: totalRev > 0 ? "#4ade80" : "#52525b" }}>Rev: ${totalRev.toFixed(2)}</span>
        <span style={{ color: "#71717a" }}>{live}L/{killed}K</span>
      </div>
    </div>
  );
}
